/**
 * LoopShellWrapper
 *
 * Wraps the application's <Routes> tree. When the active theme uses the
 * 'sidebar' layout mode (i.e. the `loop-full` theme) AND the viewer is
 * authenticated, it renders the persistent LoopShell. Otherwise it passes
 * children through untouched — preserving the existing `original` and `loop`
 * themes verbatim, and keeping the login screen free of nav chrome.
 */

import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import LoopShell from './LoopShell';

interface LoopShellWrapperProps {
  children: ReactNode;
}

// Routes where the sidebar should never appear even when authenticated.
const SHELL_BLOCKLIST = ['/login', '/zero-access', '/theme-demo'];

const LoopShellWrapper = ({ children }: LoopShellWrapperProps) => {
  const { themeConfig } = useTheme();
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  const onBlocklist = SHELL_BLOCKLIST.some(
    (path) => location.pathname === path || location.pathname.startsWith(path + '/')
  );

  const shouldShell =
    themeConfig.layoutMode === 'sidebar' && isAuthenticated && !isLoading && !onBlocklist;

  if (shouldShell) {
    return <LoopShell>{children}</LoopShell>;
  }
  return <>{children}</>;
};

export default LoopShellWrapper;
